import React, { useState } from "react";
import { Card, Form, Input, Button, message, Typography } from "antd";
import axios from "axios";
import { API_BASE_URL } from "../api/axios";

const { Title, Paragraph, Text } = Typography;

const API_URL = `${API_BASE_URL}/auth`;

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  pageBg: "#F4FBFC",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
};

const RegisterPage = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);

  const onRegister = async (values) => {
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/register`, {
        email: values.email,
        password: values.password,
      });

      if (response.data && response.data.needConfirmation) {
        setEmail(values.email);
        setStep(2);
        message.success("Registrierung erfolgreich! Bitte überprüfe deine E-Mails.");
      } else {
        message.success("Registrierung erfolgreich. Du kannst dich jetzt anmelden.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      }
    } catch (err) {
      if (err.response && err.response.data) {
        message.error(typeof err.response.data === "string" ? err.response.data : "Registrierung fehlgeschlagen.");
      } else {
        message.error("Registrierung fehlgeschlagen.");
      }
    }

    setLoading(false);
  };

  const onConfirm = async (values) => {
    setCodeLoading(true);

    try {
      await axios.post(`${API_URL}/confirm`, {
        email,
        code: values.code,
      });

      message.success("E-Mail bestätigt! Du kannst dich jetzt anmelden.");
      setTimeout(() => {
        window.location.href = "/login";
      }, 1500);
    } catch (err) {
      if (err.response && err.response.data) {
        message.error(typeof err.response.data === "string" ? err.response.data : "Bestätigung fehlgeschlagen.");
      } else {
        message.error("Bestätigung fehlgeschlagen.");
      }
    }

    setCodeLoading(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: `linear-gradient(135deg, ${COLORS.pageBg} 0%, #FFFFFF 55%, ${COLORS.primaryLight} 100%)`,
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 420,
          borderRadius: 18,
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 16px 40px rgba(31, 78, 87, 0.12)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: 16,
              background: COLORS.primary,
              margin: "0 auto 14px",
            }}
          />
          <Title level={3} style={{ margin: 0, color: COLORS.text }}>
            Konto registrieren
          </Title>
          <Text style={{ color: COLORS.muted }}>
            Starte mit deinem Smart-Routing-Zugang
          </Text>
        </div>

        {step === 1 && (
          <>
            <Paragraph style={{ color: COLORS.muted }}>
              Bitte gib deine E-Mail-Adresse und ein sicheres Passwort ein. Du erhältst anschließend einen
              Bestätigungscode per E-Mail.
            </Paragraph>

            <Form layout="vertical" onFinish={onRegister}>
              <Form.Item
                label="E-Mail"
                name="email"
                rules={[
                  { required: true, message: "Bitte E-Mail eingeben" },
                  { type: "email", message: "Bitte gültige E-Mail-Adresse eingeben" },
                ]}
              >
                <Input size="large" />
              </Form.Item>

              <Form.Item
                label="Passwort"
                name="password"
                rules={[
                  { required: true, message: "Bitte Passwort eingeben" },
                  { min: 8, message: "Mindestens 8 Zeichen" },
                ]}
                hasFeedback
              >
                <Input.Password size="large" />
              </Form.Item>

              <Form.Item
                label="Passwort wiederholen"
                name="passwordRepeat"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  { required: true, message: "Bitte Passwort bestätigen" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwörter stimmen nicht überein!"));
                    },
                  }),
                ]}
              >
                <Input.Password size="large" />
              </Form.Item>

              <Button type="primary" htmlType="submit" block loading={loading} size="large">
                Registrieren
              </Button>

              <Button
                type="link"
                htmlType="button"
                block
                onClick={() => (window.location.href = "/login")}
                style={{ marginTop: 10, color: COLORS.primary }}
              >
                Bereits ein Konto? Hier einloggen
              </Button>
            </Form>
          </>
        )}

        {step === 2 && (
          <>
            <Paragraph>
              <b>Fast geschafft!</b>
              <br />
              Bitte gib den Bestätigungscode ein, den wir dir per E-Mail geschickt haben.
              <br />
              <span style={{ fontSize: "0.95em", color: COLORS.muted }}>{email}</span>
            </Paragraph>

            <Paragraph
              style={{
                background: "#FFFBE6",
                color: "#8A6D00",
                border: "1px solid #FFE58F",
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 14,
              }}
            >
              <b>Hinweis:</b> Die Zustellung der E-Mail kann bis zu <b>5–10 Minuten</b> dauern.
              <br />
              Bitte prüfe auch deinen <b>Spam-Ordner</b>.
            </Paragraph>

            <Form layout="vertical" onFinish={onConfirm}>
              <Form.Item
                label="Bestätigungscode"
                name="code"
                rules={[{ required: true, message: "Code bitte eingeben" }]}
              >
                <Input maxLength={8} size="large" />
              </Form.Item>

              <Button type="primary" htmlType="submit" block loading={codeLoading} size="large">
                Bestätigen
              </Button>

              <Button
                type="link"
                htmlType="button"
                block
                onClick={() => setStep(1)}
                style={{ marginTop: 10, color: COLORS.primary }}
              >
                Zurück zur Registrierung
              </Button>
            </Form>
          </>
        )}
      </Card>
    </div>
  );
};

export default RegisterPage;
